import React, { useState } from 'react';
import { LoadErrorState, SegmentedControl } from '@/components/ui';
import ParentWidget, { WidgetRenderProps } from './ParentWidget';
import { COLOR_PALETTE } from './shared/barGraphUtils';
import type { LabourByType, LabourDailySummary, LabourTaskType } from '../../services/reportService';

type Measure = 'hours' | 'cost';

const MEASURE_OPTIONS: { value: Measure; label: string }[] = [
  { value: 'hours', label: 'Hours' },
  { value: 'cost', label: 'Labour $' }
];

const TYPE_COLOR: Record<LabourTaskType, string> = {
  Quantified: COLOR_PALETTE[0],
  Routine: COLOR_PALETTE[1 % COLOR_PALETTE.length],
  Overhead: COLOR_PALETTE[2 % COLOR_PALETTE.length],
  Other: COLOR_PALETTE[3 % COLOR_PALETTE.length]
};

const BAR_AREA_PX = 120;

interface Column {
  key: string;
  label: string;
  sublabel?: string;
  values: LabourByType;
  isAverage?: boolean;
}

/** Seven day columns (oldest first) plus the 28-day daily average as the last column. */
export function labourColumns(data: LabourDailySummary | undefined, measure: Measure): Column[] {
  if (!data) return [];
  // V3-023 / FMT-01, FMT-06: each day is labelled YYYY-MM-DD – no weekday or month name.
  const days: Column[] = data.days.map((day) => ({
    key: day.date,
    label: day.date,
    values: day[measure]
  }));
  return [...days, { key: 'avg28', label: '28-day', sublabel: 'avg / day', values: data.average28[measure], isAverage: true }];
}

const columnTotal = (values: LabourByType) => Object.values(values).reduce((sum, v) => sum + v, 0);

const formatValue = (value: number, measure: Measure) =>
  measure === 'cost'
    ? '$' + Math.round(value).toLocaleString('en-US')
    : value.toLocaleString('en-US', { maximumFractionDigits: 1 });

interface LabourStackedBarWidgetProps {
  id: string;
  title: string;
  data?: LabourDailySummary;
  metadata?: { loading?: boolean; error?: Error | null; lastUpdated?: Date | null };
  onRefresh?: () => void;
  requiredPermission: string;
}

/**
 * Home Labour widget: labour hours or labour $ per task type for each of the last 7 days, stacked,
 * with the 28-day daily average alongside for comparison.
 */
export function LabourStackedBarWidget({ id, title, data, metadata, onRefresh, requiredPermission }: LabourStackedBarWidgetProps) {
  const [measure, setMeasure] = useState<Measure>('hours');

  const renderBars = ({ data: summary, metadata: state }: WidgetRenderProps<LabourDailySummary>) => {
    // V3-021: a failed load says so, with Retry – never an empty chart.
    if (state?.error) {
      return <LoadErrorState what="this chart" message="Couldn't load this chart." onRetry={onRefresh} />;
    }
    const columns = labourColumns(summary, measure);
    const types = (summary?.taskTypes ?? []).filter((type) => columns.some((c) => c.values[type] > 0));
    const max = Math.max(1, ...columns.map((c) => columnTotal(c.values)));

    return (
      <div className="tw-flex tw-w-full tw-flex-col tw-gap-2 labour-stacked-bars">
        <div className="tw-flex tw-items-end tw-gap-1.5" style={{ height: BAR_AREA_PX + 18 }}>
          {columns.map((column) => {
            const total = columnTotal(column.values);
            return (
              <div key={column.key} className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-center tw-justify-end tw-h-full">
                <span className="tw-mb-0.5 tw-text-[0.65rem] tw-tabular-nums tw-text-content-muted">
                  {total > 0 ? formatValue(total, measure) : ''}
                </span>
                <div
                  className={`tw-flex tw-w-full tw-flex-col-reverse tw-overflow-hidden tw-rounded-sm ${column.isAverage ? 'tw-opacity-60 tw-outline tw-outline-1 tw-outline-dashed tw-outline-line' : ''}`}
                  style={{ height: (total / max) * BAR_AREA_PX }}
                  title={`${column.label} ${column.sublabel ?? ''}: ${formatValue(total, measure)}`}
                >
                  {types.map((type) =>
                    column.values[type] > 0 ? (
                      <div
                        key={type}
                        style={{ height: `${(column.values[type] / total) * 100}%`, backgroundColor: TYPE_COLOR[type] }}
                        title={`${column.label} ${column.sublabel ?? ''} · ${type}: ${formatValue(column.values[type], measure)}`}
                      />
                    ) : null
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="tw-flex tw-gap-1.5">
          {columns.map((column) => (
            <div key={column.key} className="tw-min-w-0 tw-flex-1 tw-text-center tw-leading-tight">
              <div className={`tw-whitespace-nowrap tw-text-[0.7rem] tw-tabular-nums ${column.isAverage ? 'tw-font-semibold' : ''} tw-text-content`}>{column.label}</div>
              <div className="tw-truncate tw-text-[0.65rem] tw-text-content-muted">{column.sublabel}</div>
            </div>
          ))}
        </div>
        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-x-3 tw-gap-y-1 tw-p-0">
          {types.map((type) => (
            <li key={type} className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-content-muted">
              <span className="tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-sm" style={{ backgroundColor: TYPE_COLOR[type] }} aria-hidden="true" />
              {type}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <ParentWidget<LabourDailySummary>
      id={id}
      title={title}
      size="medium"
      data={data}
      metadata={metadata}
      onRefresh={onRefresh}
      requiredPermission={requiredPermission}
      isEmpty={(d) => !d || [...d.days.map((day) => day.hours), d.average28.hours].every((v) => columnTotal(v) === 0)}
      headerExtra={
        <SegmentedControl<Measure>
          aria-label="Labour measure"
          options={MEASURE_OPTIONS}
          value={measure}
          onChange={setMeasure}
        />
      }
    >
      {renderBars}
    </ParentWidget>
  );
}

export default LabourStackedBarWidget;
